import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, of, ReplaySubject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CUSTOM_MASK } from '@helpers/custom-validator';
import { PortModel } from '@data-layer/models/common';
import { CodeRepository, SelfFilerRepository } from '@data-layer/repositories';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { SOFTWARE_PROVIDER_FILER_CODE } from '@constants/constants';

@Component({
  selector: 'self-filer-set-filer-code-form',
  templateUrl: './self-filer-set-filer-code-form.component.html',
  styleUrls: ['./self-filer-set-filer-code-form.component.scss'],
})
export class SelfFilerSetFilerCodeFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public companyId: number;
  @Input() public mainPort: PortModel;
  @Output() public update: EventEmitter<any> = new EventEmitter<any>();

  public open: boolean = false;
  public fg: UntypedFormGroup = new UntypedFormGroup({
    filerCode: new UntypedFormControl(null, [Validators.required, Validators.minLength(3)]),
    mainPortCode: new UntypedFormControl(null),
    isFormChecked: new UntypedFormControl(false, Validators.requiredTrue),
  });

  public errors = { required: 'FORM_ERROR.REQUIRED', minLength: 'FORM_ERROR.MIN_LENGTH' };
  public CUSTOM_MASK = CUSTOM_MASK;
  public isShowWarningFilerCodeModal: boolean = false;

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private codeRepository: CodeRepository,
    private ga: GoogleAnalyticsService,
    private selfFilerRepository: SelfFilerRepository,
  ) {}

  public ngOnInit(): void {
    this.fg
      .get('filerCode')
      .valueChanges.pipe(takeUntil(this.isDestroy), debounceTime(200))
      .subscribe((value: string) => {
        if (value && value.toUpperCase() === SOFTWARE_PROVIDER_FILER_CODE) {
          this.isShowWarningFilerCodeModal = true;
          this.fg.get('filerCode').reset();
        }
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.mainPort) {
      const mainPortCodeControl = this.fg.get('mainPortCode');
      if (this.mainPort) {
        mainPortCodeControl.clearValidators();
        mainPortCodeControl.disable();
      } else {
        mainPortCodeControl.enable();
        mainPortCodeControl.setValidators(Validators.required);
      }
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public openFilerCodeForm() {
    this.open = true;
  }

  public onSave() {
    if (!this.companyId || this.fg.invalid) {
      return;
    }
    const filerCode = this.fg.get('filerCode').value;
    this.ga.emitCustomEvent(enumGATagEventCategory.OnBoarding, enumGATagEventAction.OBFilerCodeUpdateInfo, `Filer code: ${filerCode}`);
    this.setMainPort()
      .pipe(
        switchMap(() => this.selfFilerRepository.setSelfFiler(filerCode, this.companyId)),
        takeUntil(this.isDestroy),
      )
      .subscribe(() => {
        this.update.emit();
      });
  }

  public searchPort() {
    return this.codeRepository.searchDomesticPorts.bind(this.codeRepository);
  }

  private setMainPort(): Observable<any> {
    if (this.fg.get('mainPortCode').enabled) {
      const code = this.fg.get('mainPortCode').value;
      return this.selfFilerRepository.updateAdditionalPorts([code], this.companyId).pipe(
        tap(() => {
          this.ga.emitCustomEvent(
            enumGATagEventCategory.OnBoarding,
            enumGATagEventAction.OBPermittedPortsUpdateInfo,
            `Permitted ports: ${code}`,
          );
        }),
        switchMap(() => this.selfFilerRepository.setMainPort(code, this.companyId)),
        tap(() => {
          this.ga.emitCustomEvent(enumGATagEventCategory.OnBoarding, enumGATagEventAction.OBMainPortUpdateInfo, `Main port: ${code}`);
        }),
      );
    }
    return of(null);
  }
}
